import React, { useState, useEffect, useRef } from 'react';
import { authFetch } from '../services/authFetch';
import MarkdownRenderer from '../components/MicroLearning/MarkdownRenderer';
import ChatMCQ from '../components/MicroLearning/ChatMCQ';
import { aiTutorAPI, AskLomiRequest, AskLomiResponse, AIUsageStatus, track } from '../services/api';
import EthiopianFlag from '../components/EthiopianFlag';
import './TelegramAskLomi.css';

// Telegram WebApp type declarations
declare global {
  interface Window {
    Telegram?: {
      WebApp: {
        initData: string;
        initDataUnsafe: { user?: { id: number; first_name: string } };
        ready: () => void;
        expand: () => void;
        close: () => void;
        themeParams: Record<string, string | undefined>;
        BackButton: { show: () => void; hide: () => void; onClick: (cb: () => void) => void };
        isExpanded: boolean;
      };
    };
  }
}

interface ChatMessage {
  role: 'user' | 'ai';
  text: string;
  followUps?: Array<{ label: string; prompt: string }>;
  interactiveComponent?: any;  // Full interactive component from AI (MCQ, flashcard, etc.)
  amharicTranslation?: string;  // Code-switched Amharic re-render, shown under the bubble
}

interface QuickQuestion {
  icon: string;
  label: string;
  question: string;
}

// Quiz review data
interface QuizReviewQuestion {
  question: string;
  options: Record<string, string>;
  correct_answer: string;
  student_answer: string;
  is_correct: boolean;
  explanation?: string;
}

// Full term data fetched from API
interface TermData {
  term: string;
  brief_definition?: string;
  brief?: string;
  definition?: string;
  detailed_explanation?: string;
  detailed?: string;
  exam_tip?: string;
  memorization_priority?: string;
  related_terms?: string[];
  ethiopia_context?: string;
}

const TelegramAskLomi: React.FC = () => {
  const [isLoading, setIsLoading] = useState(true);
  const [authError, setAuthError] = useState<string | null>(null);
  const [studentId, setStudentId] = useState<string>('guest');
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const [conversationId, setConversationId] = useState<string | undefined>();
  const [translatingIndex, setTranslatingIndex] = useState<number | null>(null);
  const [usage, setUsage] = useState<AIUsageStatus | null>(null);
  const [termData, setTermData] = useState<TermData | null>(null);
  const [allTerms, setAllTerms] = useState<TermData[]>([]);
  const [quizReview, setQuizReview] = useState<QuizReviewQuestion[]>([]);
  const [examQuestion, setExamQuestion] = useState<any>(null);  // Past exam question data
  const chatEndRef = useRef<HTMLDivElement>(null);

  // Parse URL params for context
  const params = new URLSearchParams(window.location.search);
  const subject = params.get('subject') || '';
  const topic = params.get('topic') || '';
  const moduleId = params.get('module_id') || undefined;
  const term = params.get('term') || '';
  const termDef = params.get('term_def') || '';
  const componentId = params.get('component_id') || undefined;
  const questionId = params.get('question_id') || undefined;
  const studentAnswer = params.get('answer') || '';
  const componentType = params.get('type') || undefined;
  // Source of this Mini App launch — set by the bot's central builder.
  // Used for logging + future verification against an expected contract.
  const source = params.get('source') || '';
  // Knowledge-check summary (only populated for source=kc_results).
  const kcScoreCorrect = params.get('score_correct') || '';
  const kcScoreTotal = params.get('score_total') || '';
  const kcScorePct = params.get('score_pct') || '';
  const kcFocusTopics = (params.get('focus_topics') || '')
    .split(',')
    .map(t => t.trim())
    .filter(Boolean);

  // Context-aware quick questions — matches web app's AIHelpPanel getQuickQuestions()
  const getQuickQuestions = (): QuickQuestion[] => {
    const topicCtx = topic ? ` for ${topic}` : '';

    if (componentType === 'key_definitions') {
      if (term) {
        // Single term — just input box, no predefined buttons
        // Student will type what they need
        return [];
      }
      // All terms for a topic — one focused action
      const termNames = allTerms.map(d => d.term || d.brief || '').filter(Boolean);
      const termsContext = termNames.length > 0
        ? `The terms are: ${termNames.join(', ')}.`
        : '';
      return [
        { icon: '📝', label: 'Quiz me on these', question: `Quiz me on the key terms${topicCtx}. ${termsContext} Give me a multiple choice question.` },
      ];
    }
    if (componentType === 'practice_mcq' || componentType === 'real_eslce_question') {
      if (examQuestion) {
        const isCorrect = studentAnswer.toUpperCase() === (examQuestion.metadata?.correct_answer || '').toUpperCase();
        if (isCorrect) {
          return [
            { icon: '🎯', label: 'Similar question', question: `Give me a similar practice question about ${examQuestion.metadata?.topic || topic || 'this topic'}.` },
          ];
        } else {
          return [
            { icon: '❓', label: 'Why is my answer wrong?', question: `I answered ${studentAnswer} but the correct answer is ${examQuestion.metadata?.correct_answer}. Explain why my answer is wrong and the correct one is right.` },
          ];
        }
      }
      return [];
    }
    if (componentType === 'assessment_set') {
      // Each question in the review card has its own "Explain this" button.
      // These chips cover whole-quiz actions.
      const chips: QuickQuestion[] = [];
      const wrongCount = quizReview.filter(q => !q.is_correct).length;
      if (wrongCount > 0) {
        chips.push({
          icon: '🔍',
          label: 'Walk through wrong ones',
          question: `Walk me through every question I got wrong on this quiz, one at a time.`,
        });
      }
      chips.push({
        icon: '🎯',
        label: 'Similar quiz',
        question: `Generate a similar practice quiz${topicCtx}.`,
      });
      return chips;
    }
    if (componentType === 'eslce_intel') {
      return [
        { icon: '🎯', label: 'More Strategies', question: `What other exam strategies should I know${topicCtx}?` },
        { icon: '⚠️', label: 'Practice Trap', question: 'Create a question that uses one of these exam traps so I can practice.' },
      ];
    }
    if (componentType === 'diagnostic' || componentType === 'knowledge_check') {
      return [
        {
          icon: '🎯',
          label: 'Where to focus',
          question: `Based on my knowledge check results, what should I focus on first${subject ? ` in ${subject}` : ''}?`,
        },
        {
          icon: '📚',
          label: 'Study plan',
          question: `Make me a study plan for ${subject || 'this subject'} based on my knowledge check.`,
        },
      ];
    }
    // General / from home
    return [
      { icon: '💡', label: 'Study Help', question: subject ? `What should I focus on studying in ${subject}?` : 'What should I focus on studying for the ESLCE?' },
      { icon: '🎯', label: 'Exam Tips', question: `What topics are most important for the ${subject || 'ESLCE'} exam?` },
      { icon: '📚', label: 'Practice', question: subject ? `Give me a practice question for ${subject}.` : 'Give me a practice question.' },
    ];
  };

  // Auto-scroll to bottom
  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, sending]);

  // Initialize Telegram + Auth
  useEffect(() => {
    const init = async () => {
      // Load Telegram SDK
      if (!window.Telegram?.WebApp) {
        if (!document.querySelector('script[src*="telegram-web-app"]')) {
          const script = document.createElement('script');
          script.src = 'https://telegram.org/js/telegram-web-app.js';
          document.head.appendChild(script);
        }
        // Wait for SDK
        await new Promise<void>((resolve) => {
          let attempts = 0;
          const check = setInterval(() => {
            if (window.Telegram?.WebApp || ++attempts > 20) {
              clearInterval(check);
              resolve();
            }
          }, 100);
        });
      }

      const tg = window.Telegram?.WebApp;
      if (tg) {
        tg.ready();
        tg.expand();
        try {
          tg.BackButton.show();
          tg.BackButton.onClick(() => tg.close());
        } catch {}
      }

      // Open-event log — matches the bot's central builder log so we can
      // correlate what was sent with what was received.
      console.info('[AskLomi] open', {
        source, componentType, subject, topic,
        hasModuleId: !!moduleId, hasComponentId: !!componentId,
        hasQuestionId: !!questionId, hasStudentAnswer: !!studentAnswer,
        hasTerm: !!term,
      });

      // Authenticate. Keep a LOCAL `resolvedStudentId` alongside setStudentId
      // because React state updates are async — inside this init() closure the
      // studentId variable stays bound to its initial 'guest' value, which
      // previously poisoned every student_id-dependent fetch below.
      let resolvedStudentId = 'guest';
      const initData = tg?.initData;
      if (initData) {
        try {
          const resp = await fetch('/api/auth/telegram/webapp', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ init_data: initData }),
          });
          const data = await resp.json();
          if (data.token) {
            localStorage.setItem('access_token', data.token);
            if (data.refresh_token) localStorage.setItem('refresh_token', data.refresh_token);
            if (data.student_id) {
              resolvedStudentId = data.student_id;
              setStudentId(data.student_id);
            }
          } else {
            setAuthError('Authentication failed.');
          }
        } catch (e) {
          setAuthError('Could not connect. Please try again.');
        }
      } else {
        // Not in Telegram — check existing token
        if (!localStorage.getItem('access_token')) {
          setAuthError('Please open this from the Lomi Telegram bot.');
        }
      }
      console.info('[AskLomi] auth', {
        resolvedStudentId: resolvedStudentId === 'guest' ? 'guest' : 'resolved',
        hasToken: !!localStorage.getItem('access_token'),
      });

      // Load AI usage
      try {
        const u = await aiTutorAPI.getUsage();
        setUsage(u);
      } catch {}

      // Fetch key_definitions data from API for context display
      if (moduleId && componentType === 'key_definitions') {
        try {
          const token = localStorage.getItem('access_token');
          const sessionResp = await authFetch(
            `/api/micro-learning/modules/${moduleId}/session?student_id=${resolvedStudentId}&review=true`,
            { headers: token ? { Authorization: `Bearer ${token}` } : {} }
          );
          const sessionData = await sessionResp.json();
          const compList = sessionData?.component_list || [];
          const kdComp = compList.find((c: any) => c.component_type === 'key_definitions');
          if (kdComp) {
            const jumpResp = await authFetch(
              `/api/micro-learning/modules/${moduleId}/jump-to-component?student_id=${resolvedStudentId}&component_index=${kdComp.order_index}&review=true`,
              { method: 'POST', headers: token ? { Authorization: `Bearer ${token}` } : {} }
            );
            const jumpData = await jumpResp.json();
            const defs = jumpData?.current_component?.content_data?.definitions || [];
            console.info('[AskLomi] key_definitions fetched', { count: defs.length, moduleId, componentType });
            // Store all definitions for list display
            setAllTerms(defs);
            // If a specific term was requested, find it
            let foundTerm: any = null;
            if (term) {
              foundTerm = defs.find((d: any) =>
                (d.term || d.name || '').toLowerCase() === term.toLowerCase()
              );
              if (foundTerm) setTermData(foundTerm);
            }
            // Seed intro + follow-up buttons so the AI gets a context-rich
            // prompt when the student taps, instead of relying on a vague
            // "Yes" reply with no backend context.
            if (foundTerm) {
              const briefDef = foundTerm.brief_definition || foundTerm.brief || foundTerm.definition || '';
              setMessages([{
                role: 'ai',
                text: `Want me to explain **${foundTerm.term}** with examples, or quiz you on it?`,
                followUps: [
                  {
                    label: '📖 Explain with examples',
                    prompt: `Explain "${foundTerm.term}" with concrete, easy-to-understand examples for an ESLCE student. Brief definition: ${briefDef}`,
                  },
                  {
                    label: '📝 Quiz me on this',
                    prompt: `Quiz me on "${foundTerm.term}". Give me one multiple-choice question to test if I understand it.`,
                  },
                ],
              }]);
            } else if (defs.length > 0) {
              const termNames = defs.map((d: any) => d.term || d.brief || '').filter(Boolean);
              setMessages([{
                role: 'ai',
                text: `${defs.length} key terms loaded for ${topic || subject || 'this topic'}. Want me to quiz you, or explain one in detail?`,
                followUps: [
                  {
                    label: '📝 Quiz me on these',
                    prompt: `Quiz me on the key terms${topic ? ` for ${topic}` : ''}. The terms are: ${termNames.join(', ')}. Give me a multiple-choice question.`,
                  },
                ],
              }]);
            }
          }
        } catch (e) {
          console.error('[AskLomi] key_definitions fetch failed', e);
        }
      }

      // Fetch quiz review data for assessment_set context
      if (moduleId && componentId && componentType === 'assessment_set') {
        try {
          const token = localStorage.getItem('access_token');
          const hdrs: Record<string, string> = token ? { Authorization: `Bearer ${token}` } : {};
          // Get session with student responses
          const sessionResp = await authFetch(
            `/api/micro-learning/modules/${moduleId}/session?student_id=${resolvedStudentId}&review=true`,
            { headers: hdrs }
          );
          const sessionData = await sessionResp.json();
          const compList = sessionData?.component_list || [];
          // Find this specific assessment_set and its student_response
          const quizComp = compList.find((c: any) => c.component_id === componentId);
          const studentResponse = quizComp?.student_response?.response_data || {};
          console.info('[AskLomi] assessment_set session loaded', {
            components: compList.length,
            foundQuiz: !!quizComp,
            hasResponse: Object.keys(studentResponse).length > 0,
          });

          // Jump to component to get full question data
          if (quizComp) {
            const jumpResp = await authFetch(
              `/api/micro-learning/modules/${moduleId}/jump-to-component?student_id=${resolvedStudentId}&component_index=${quizComp.order_index}&review=true`,
              { method: 'POST', headers: hdrs }
            );
            const jumpData = await jumpResp.json();
            const questions = jumpData?.current_component?.content_data?.questions || [];

            // Build review data: match questions with student answers
            const reviewData: QuizReviewQuestion[] = questions.map((q: any, i: number) => {
              const studentAns = studentResponse[String(i)] || '';
              const correctAns = q.correct_answer || '';
              const opts = q.options || {};
              const optionsDict = Array.isArray(opts)
                ? Object.fromEntries(opts.map((o: any, j: number) => [o.label || String.fromCharCode(65 + j), o.text || String(o)]))
                : opts;
              return {
                question: q.question || q.question_text || q.stem || '',
                options: optionsDict,
                correct_answer: correctAns,
                student_answer: studentAns,
                is_correct: studentAns.toUpperCase() === correctAns.toUpperCase(),
                explanation: q.explanation,
              };
            });
            setQuizReview(reviewData);
            console.info('[AskLomi] quiz review built', {
              total: reviewData.length,
              correct: reviewData.filter(q => q.is_correct).length,
            });
            // Seed intro + context-rich follow-up buttons so taps reach the
            // backend with everything the AI needs in one shot.
            const correct = reviewData.filter(q => q.is_correct).length;
            const total = reviewData.length;
            const wrongIdxs = reviewData
              .map((q, i) => (q.is_correct ? null : i + 1))
              .filter((x): x is number => x !== null);
            let introText: string;
            const followUps: Array<{ label: string; prompt: string }> = [];
            const topicLabel = topic || subject || 'this topic';
            if (total === 0) {
              introText = `I'm ready to help with your quiz. What would you like to dig into?`;
            } else if (wrongIdxs.length === 0) {
              introText = `Nice work — you got ${correct}/${total}! Want to try a harder version, or explore one of these questions deeper?`;
              followUps.push(
                { label: '🎯 Harder version', prompt: `Generate a harder practice quiz for ${topicLabel}.` },
                { label: '💡 Explore deeper', prompt: `Pick the most conceptually rich question from this quiz and walk me through advanced ways the same idea could be tested on the ESLCE.` },
              );
            } else {
              const wrongLabel = wrongIdxs.length === 1
                ? `Q${wrongIdxs[0]}`
                : wrongIdxs.length === 2
                  ? `Q${wrongIdxs[0]} and Q${wrongIdxs[1]}`
                  : `Q${wrongIdxs.slice(0, -1).join(', Q')}, and Q${wrongIdxs[wrongIdxs.length - 1]}`;
              introText = `You got ${correct}/${total} — ${wrongLabel} ${wrongIdxs.length === 1 ? 'was' : 'were'} tricky. Tap **Explain this** on any below, or ask me anything.`;
              // Build a compact summary of every wrong question so the prompt is self-contained.
              const wrongDetails = reviewData
                .map((q, i) => q.is_correct ? null : `Q${i + 1}: "${q.question.slice(0, 140)}" — I answered ${q.student_answer || '(blank)'}, correct is ${q.correct_answer}`)
                .filter(Boolean)
                .join('\n');
              followUps.push(
                {
                  label: '🔍 Walk through wrong ones',
                  prompt: `Walk me through every question I got wrong on this quiz, one at a time. Here are the wrong ones:\n${wrongDetails}\n\nFor each, briefly explain why my answer was wrong and why the correct one is right.`,
                },
                { label: '🎯 Similar quiz', prompt: `Generate a similar practice quiz for ${topicLabel}.` },
              );
            }
            setMessages([{ role: 'ai', text: introText, followUps }]);
          }
        } catch (e) {
          console.error('[AskLomi] assessment_set fetch failed', e);
        }
      }

      // Fetch past exam question by ID
      if (questionId && componentType === 'real_eslce_question') {
        try {
          const token = localStorage.getItem('access_token');
          const resp = await authFetch(`/api/questions/${questionId}`, {
            headers: token ? { Authorization: `Bearer ${token}` } : {},
          });
          const data = await resp.json();
          console.info('[AskLomi] real_eslce_question fetched', {
            success: data.success,
            hasQuestion: !!data.question,
          });
          if (data.success && data.question) {
            setExamQuestion(data.question);
            // Seed intro + follow-up buttons. The buttons send fully-formed
            // prompts so the backend AI gets the question, the student's
            // answer, and the correct answer — not just a vague "Yes".
            const correctAns = (data.question.metadata?.correct_answer || '').toUpperCase();
            const studentUpper = studentAnswer.toUpperCase();
            const qText = data.question.question_text || '';
            const qTopic = data.question.metadata?.topic || topic || 'this topic';
            if (studentUpper && correctAns) {
              if (studentUpper === correctAns) {
                setMessages([{
                  role: 'ai',
                  text: `Nice — **${studentAnswer}** was correct. Want me to explain the reasoning, or try a similar question?`,
                  followUps: [
                    {
                      label: '💡 Explain reasoning',
                      prompt: `Explain the reasoning behind the correct answer (${correctAns}) for this ESLCE question:\n\n"${qText}"\n\nGo deeper than the stored explanation — show me the underlying concept.`,
                    },
                    {
                      label: '🎯 Similar question',
                      prompt: `Give me a similar ESLCE-style practice question for ${qTopic}, then check my answer.`,
                    },
                  ],
                }]);
              } else {
                setMessages([{
                  role: 'ai',
                  text: `You answered **${studentAnswer}**, but the correct answer is **${correctAns}**. Want me to walk through why?`,
                  followUps: [
                    {
                      label: '🔍 Yes, explain why',
                      prompt: `On this ESLCE question:\n\n"${qText}"\n\nI answered ${studentAnswer} but the correct answer is ${correctAns}. Explain why my answer is wrong and why ${correctAns} is right.`,
                    },
                    {
                      label: '🎯 Similar question',
                      prompt: `Give me a similar ESLCE-style practice question for ${qTopic} so I can try again.`,
                    },
                  ],
                }]);
              }
            } else {
              setMessages([{
                role: 'ai',
                text: `I've loaded this past exam question. What would you like help with?`,
                followUps: [
                  {
                    label: '💡 Explain this question',
                    prompt: `Walk me through this ESLCE question step by step:\n\n"${qText}"\n\nThe correct answer is ${correctAns}. Explain why.`,
                  },
                  {
                    label: '🎯 Similar question',
                    prompt: `Give me a similar ESLCE-style practice question for ${qTopic}.`,
                  },
                ],
              }]);
            }
          }
        } catch (e) {
          console.error('[AskLomi] real_eslce_question fetch failed', e);
        }
      }

      // Seed an intro for diagnostic / KC results. The bot passes the actual
      // score + focus topics via URL params (source=kc_results) so the greeting
      // shows real numbers instead of a generic "I've seen your results".
      if (componentType === 'diagnostic' || componentType === 'knowledge_check') {
        const subjectLabel = subject || 'this subject';
        const hasScore = kcScoreCorrect && kcScoreTotal;
        const scoreLine = hasScore
          ? `**${kcScoreCorrect}/${kcScoreTotal}${kcScorePct ? ` (${kcScorePct}%)` : ''}** on your ${subjectLabel} knowledge check`
          : `your ${subjectLabel} knowledge check`;
        const focusLine = kcFocusTopics.length > 0
          ? `\n\nYour focus areas: ${kcFocusTopics.slice(0, 5).map(t => `**${t}**`).join(', ')}.`
          : '';
        const focusPrompt = kcFocusTopics.length > 0
          ? `My weak topics are: ${kcFocusTopics.slice(0, 5).join(', ')}. Which should I focus on first and why?`
          : `Based on my knowledge check results, what should I focus on first${subject ? ` in ${subject}` : ''}? Give me a prioritized list of 3-5 topics.`;
        const planPrompt = kcFocusTopics.length > 0
          ? `Make me a one-week study plan for ${subjectLabel} focusing on these weak topics: ${kcFocusTopics.slice(0, 5).join(', ')}.`
          : `Make me a one-week study plan for ${subjectLabel} based on my knowledge check results.`;
        setMessages([{
          role: 'ai',
          text: `You scored ${scoreLine}.${focusLine}\n\nWant help with your focus areas, or a study plan?`,
          followUps: [
            { label: '🎯 Where to focus', prompt: focusPrompt },
            { label: '📚 Study plan', prompt: planPrompt },
          ],
        }]);
      }

      setIsLoading(false);
    };
    init();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Apply Telegram theme
  useEffect(() => {
    const tp = window.Telegram?.WebApp?.themeParams;
    if (tp) {
      const root = document.documentElement;
      if (tp.bg_color) root.style.setProperty('--tg-bg', tp.bg_color);
      if (tp.text_color) root.style.setProperty('--tg-text', tp.text_color);
      if (tp.button_color) root.style.setProperty('--tg-button', tp.button_color);
      if (tp.secondary_bg_color) root.style.setProperty('--tg-bg2', tp.secondary_bg_color);
    }
  }, []);

  // No auto-send — show stored explanation from API data instead
  // LLM is only called when student explicitly taps a chip or types a question

  // `question` is the actual prompt sent to the AI (may be long, may contain
  // raw LaTeX or markdown). `displayText`, when provided, is what's shown in
  // the user's chat bubble — use it to keep quick-chip bubbles concise instead
  // of echoing the whole context-rich prompt. If omitted, the bubble shows
  // the question text.
  const sendMessage = async (question: string, displayText?: string) => {
    if (!question.trim() || sending) return;
    if (usage && !usage.can_send) return;

    setSending(true);
    setMessages(prev => [...prev, { role: 'user', text: displayText || question }]);
    setInput('');

    try {
      const request: AskLomiRequest = {
        question,
        context_type: moduleId ? 'module_session' : (questionId ? 'eslce_quiz' : 'general'),
        module_id: moduleId,
        component_id: componentId,
        question_id: questionId,
        conversation_id: conversationId,
        // Entry-point surface (from the bot/channel deep-link URL), or "home"
        // for the generic mini-app entry — persisted for per-surface analytics.
        source: source || 'home',
      };

      const response: AskLomiResponse = await aiTutorAPI.ask(request);

      if (response.success) {
        const aiText = response.content || '';
        const hasInteractive = !!response.interactive_component;
        const msg: ChatMessage = {
          role: 'ai',
          text: aiText || (hasInteractive ? '' : 'I received your question but couldn\'t generate a response. Please try again.'),
          followUps: response.follow_up_prompts,
          interactiveComponent: response.interactive_component,
        };
        setMessages(prev => [...prev, msg]);
        if (response.conversation_id) setConversationId(response.conversation_id);
        if (response.usage) setUsage(response.usage);
      } else {
        setMessages(prev => [...prev, { role: 'ai', text: 'Sorry, I couldn\'t generate a response. Please try again.' }]);
      }
    } catch (err: any) {
      if (err.response?.status === 429) {
        setMessages(prev => [...prev, { role: 'ai', text: 'You\'ve reached your daily message limit. Come back tomorrow or upgrade your plan!' }]);
      } else {
        setMessages(prev => [...prev, { role: 'ai', text: 'Something went wrong. Please try again.' }]);
      }
    }

    setSending(false);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage(input);
    }
  };

  // Re-render an AI answer into code-switched Amharic, attached to the same
  // bubble (keeps the English visible). Mirrors AIHelpPanel; surface stays
  // 'chat_bubble' so it joins the same funnel, channel marks the Mini App.
  const handleTranslateAmharic = async (text: string, index: number) => {
    if (translatingIndex !== null) return;
    track('ask_lomi.translate_amharic.clicked', {
      surface: 'chat_bubble',
      channel: 'telegram_miniapp',
      message_index: index,
      char_count: text.length,
    });
    setTranslatingIndex(index);
    try {
      const result = await aiTutorAPI.translateAmharic(text);
      if (result?.amharic_text) {
        setMessages(prev =>
          prev.map((m, i) =>
            i === index ? { ...m, amharicTranslation: result.amharic_text } : m
          )
        );
      }
    } catch (err) {
      console.error('Amharic translation failed:', err);
    } finally {
      setTranslatingIndex(null);
    }
  };

  // ── Loading ──
  if (isLoading) {
    return (
      <div className="tg-lomi-loading">
        <div className="tg-spinner" />
        <p>Loading Lomi AI...</p>
      </div>
    );
  }

  // ── Auth Error ──
  if (authError) {
    return (
      <div className="tg-lomi-error">
        <p>⚠️ {authError}</p>
        <button onClick={() => window.Telegram?.WebApp?.close()}>Close</button>
      </div>
    );
  }

  const quickQuestions = getQuickQuestions();

  // ── Chat UI ──
  return (
    <div className="tg-lomi">
      {/* Header — subtitle reflects whichever context actually loaded */}
      <header className="tg-lomi-header">
        <span className="tg-lomi-icon">🍋</span>
        <div>
          <h1>Lomi AI</h1>
          <p className="tg-lomi-subtitle">
            {(() => {
              if (componentType === 'assessment_set' && quizReview.length > 0) {
                const correct = quizReview.filter(q => q.is_correct).length;
                return `Quiz Review · ${correct}/${quizReview.length}${topic ? ` · ${topic}` : ''}`;
              }
              if (componentType === 'real_eslce_question' && examQuestion) {
                const year = examQuestion.exam?.year_ec || examQuestion.exam?.year || '';
                return `Past Exam${year ? ` · ESLCE ${year}` : ''}${topic ? ` · ${topic}` : ''}`;
              }
              if (componentType === 'key_definitions' && termData) {
                return `Key Term · ${termData.term}`;
              }
              if (componentType === 'key_definitions' && allTerms.length > 0) {
                return `Key Terms · ${allTerms.length} terms${topic ? ` · ${topic}` : ''}`;
              }
              if (componentType === 'practice_mcq') {
                return `Practice Question${topic ? ` · ${topic}` : subject ? ` · ${subject}` : ''}`;
              }
              if (componentType === 'diagnostic' || componentType === 'knowledge_check') {
                return `Knowledge Check${subject ? ` · ${subject}` : ''}`;
              }
              if (subject && topic) return `${subject} · ${topic}`;
              if (subject) return `${subject} study assistant`;
              return 'Your ESLCE study assistant';
            })()}
          </p>
        </div>
      </header>

      {/* Chat area — everything scrolls together */}
      <div className="tg-lomi-chat">
        {/* Quiz review — show questions with student answers */}
        {componentType === 'assessment_set' && quizReview.length > 0 && (
          <div className="tg-quiz-review">
            <p className="tg-quiz-review-header">
              📋 Quiz Review · {topic || subject}
              {' · '}{quizReview.filter(q => q.is_correct).length}/{quizReview.length} correct
            </p>
            {quizReview.map((q, i) => (
              <div key={i} className={`tg-quiz-review-q ${q.is_correct ? 'correct' : 'wrong'}`}>
                <div className="tg-quiz-review-status">
                  {q.is_correct ? '✅' : '❌'} Q{i + 1}
                </div>
                <div className="tg-quiz-review-text"><MarkdownRenderer>{q.question}</MarkdownRenderer></div>
                <div className="tg-quiz-review-answers">
                  {Object.entries(q.options).map(([label, text]) => (
                    <div key={label} className={`tg-quiz-review-opt ${
                      label.toUpperCase() === q.correct_answer.toUpperCase() ? 'correct-opt' :
                      label.toUpperCase() === q.student_answer.toUpperCase() ? 'wrong-opt' : ''
                    }`}>
                      <span className="tg-quiz-opt-label">{label}</span> {String(text)}
                    </div>
                  ))}
                </div>
                {q.explanation && (
                  <div className="tg-quiz-review-exp">💡 <MarkdownRenderer>{q.explanation || ''}</MarkdownRenderer></div>
                )}
                <button
                  className="tg-quiz-review-ask"
                  onClick={() => sendMessage(
                    q.is_correct
                      ? `Tell me more about Q${i+1}: "${q.question.substring(0, 100)}". I got it right (${q.correct_answer}), but I want to understand it deeper.`
                      : `Explain Q${i+1}: "${q.question.substring(0, 100)}". I answered ${q.student_answer} but the correct answer is ${q.correct_answer}. Why?`
                  )}
                >
                  🍋 {q.is_correct ? 'Tell me more' : 'Explain this'}
                </button>
              </div>
            ))}
          </div>
        )}

        {/* Past exam question — shown when opened from answered question */}
        {examQuestion && componentType === 'real_eslce_question' && (
          <div className="tg-exam-question-card">
            <p className="tg-exam-q-header">
              📝 ESLCE {examQuestion.exam?.year_ec || examQuestion.exam?.year || ''} · {examQuestion.metadata?.topic || ''}
            </p>
            <div className="tg-exam-q-text">
              <MarkdownRenderer>{examQuestion.question_text || ''}</MarkdownRenderer>
            </div>
            <div className="tg-exam-q-options">
              {(examQuestion.options || []).map((opt: any) => {
                const isCorrect = opt.is_correct || opt.label?.toUpperCase() === (examQuestion.metadata?.correct_answer || '').toUpperCase();
                const isStudentPick = studentAnswer && opt.label?.toUpperCase() === studentAnswer.toUpperCase();
                const isWrongPick = isStudentPick && !isCorrect;
                return (
                  <div key={opt.label} className={`tg-exam-q-opt ${isCorrect ? 'correct-opt' : ''} ${isWrongPick ? 'wrong-opt' : ''}`}>
                    <span className="tg-exam-q-label">{opt.label}</span>
                    <MarkdownRenderer>{opt.text || ''}</MarkdownRenderer>
                    {isCorrect && <span className="tg-exam-q-badge">✓</span>}
                    {isWrongPick && <span className="tg-exam-q-badge">✗ Your answer</span>}
                  </div>
                );
              })}
            </div>
            {examQuestion.metadata?.explanation && (
              <div className="tg-exam-q-exp">
                💡 <MarkdownRenderer>{examQuestion.metadata.explanation}</MarkdownRenderer>
              </div>
            )}
          </div>
        )}

        {/* All terms list — shown when opened from key terms list (no specific term) */}
        {!term && componentType === 'key_definitions' && allTerms.length > 0 && (
        <div className="tg-terms-list">
          <p className="tg-terms-list-header">📖 Key Terms · {topic || subject}</p>
          {allTerms.map((d, i) => (
            <div key={i} className="tg-terms-list-item">
              <span className="tg-terms-list-num">{i + 1}.</span>
              <div>
                <strong>{d.term || d.brief || ''}</strong>
                {(d.brief_definition || d.brief || d.definition) && (
                  <div className="tg-terms-list-def"><MarkdownRenderer>{d.brief_definition || d.brief || d.definition || ''}</MarkdownRenderer></div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Single term card — shows full term data from API */}
      {term && (
        <div className="tg-term-card">
          <h3 className="tg-term-name">📖 {termData?.term || term}</h3>

          {/* Brief definition */}
          {(termData?.brief_definition || termData?.brief || termData?.definition || termDef) && (
            <div className="tg-term-brief">
              <MarkdownRenderer>{termData?.brief_definition || termData?.brief || termData?.definition || termDef}</MarkdownRenderer>
            </div>
          )}

          {/* Detailed explanation — the stored one, no LLM needed */}
          {(termData?.detailed_explanation || termData?.detailed) && (
            <div className="tg-term-detailed">
              <MarkdownRenderer>{termData.detailed_explanation || termData.detailed || ''}</MarkdownRenderer>
            </div>
          )}

          {/* Exam tip */}
          {termData?.exam_tip && (
            <div className="tg-term-tip">💡 <strong>Exam Tip:</strong> <MarkdownRenderer>{termData.exam_tip}</MarkdownRenderer></div>
          )}

          {/* Related terms */}
          {termData?.related_terms && termData.related_terms.length > 0 && (
            <p className="tg-term-related">🔗 Related: {termData.related_terms.join(', ')}</p>
          )}

          {/* Priority badge */}
          {termData?.memorization_priority && (
            <span className={`tg-term-priority tg-priority-${termData.memorization_priority}`}>
              {termData.memorization_priority === 'must_know' || termData.memorization_priority === 'high' ? '⭐ Must Know' :
               termData.memorization_priority === 'important' || termData.memorization_priority === 'medium' ? '📌 Important' :
               '💡 Good to Know'}
            </span>
          )}
        </div>
      )}

        {/* Quick questions — shown only before first message */}
        {messages.length === 0 && !sending && quickQuestions.length > 0 && (
          <div className="tg-lomi-chips-bar">
            {quickQuestions.map((q, i) => (
              <button
                key={i}
                className="tg-chip"
                onClick={() => sendMessage(q.question, `${q.icon} ${q.label}`)}
              >
                <span>{q.icon}</span> {q.label}
              </button>
            ))}
          </div>
        )}

        {/* Welcome text — only when no terms displayed and no messages */}
        {messages.length === 0 && !sending && !term && componentType !== 'key_definitions' && (
          <div className="tg-lomi-welcome">
            <p className="tg-lomi-prompt">What would you like help with?</p>
          </div>
        )}

        {/* Messages */}
        {messages.map((msg, i) => (
          <div key={i} className={`tg-msg tg-msg-${msg.role}`}>
            {msg.role === 'ai' && <span className="tg-msg-avatar">🍋</span>}
            <div className="tg-msg-bubble">
              {/* Render both user and AI through MarkdownRenderer so any
                  $math$ / **markdown** in either side shows up styled
                  instead of raw. User text is usually short (often just
                  the follow-up button's label). */}
              {msg.text && <MarkdownRenderer>{msg.text}</MarkdownRenderer>}

              {/* Interactive MCQ from AI — same pattern as web app's AIHelpPanel */}
              {msg.interactiveComponent && msg.interactiveComponent.component_type === 'mcq' && msg.interactiveComponent.data && (
                <div className="tg-msg-mcq">
                  <ChatMCQ
                    componentId={msg.interactiveComponent.component_id}
                    questionText={msg.interactiveComponent.data.question_text || msg.interactiveComponent.data.question || ''}
                    options={(msg.interactiveComponent.data.options || []).map((o: any) => ({
                      label: o.label || '',
                      text: o.text || String(o),
                    }))}
                    correctAnswer={
                      (msg.interactiveComponent.data.options || []).find((o: any) => o.is_correct)?.label
                      || msg.interactiveComponent.data.correct_answer
                      || ''
                    }
                    explanation={msg.interactiveComponent.data.explanation}
                    topic={msg.interactiveComponent.data.topic}
                    difficulty={msg.interactiveComponent.data.difficulty}
                    onAnswerSubmit={async (compId, answer) => {
                      try {
                        const resp = await aiTutorAPI.submitAnswer({
                          component_id: compId,
                          answer,
                          conversation_id: conversationId,
                        });
                        if (resp.success) {
                          return {
                            isCorrect: resp.is_correct,
                            correctAnswer: resp.correct_answer,
                            explanation: resp.explanation || '',
                            followUpPrompts: resp.follow_up_prompts,
                          };
                        }
                      } catch (e) { console.error('Submit answer failed:', e); }
                      return null;
                    }}
                    onFollowUp={(prompt) => sendMessage(prompt)}
                  />
                </div>
              )}

              {/* Follow-up suggestions — bubble shows the short label, AI
                  gets the full context-rich prompt */}
              {!msg.interactiveComponent && msg.followUps && msg.followUps.length > 0 && (
                <div className="tg-msg-followups">
                  {msg.followUps.slice(0, 3).map((fp, j) => (
                    <button
                      key={j}
                      className="tg-followup"
                      onClick={() => sendMessage(fp.prompt, fp.label)}
                    >
                      {fp.label}
                    </button>
                  ))}
                </div>
              )}

              {/* Amharic translate — text AI bubbles only (skip interactive
                  MCQs). Translation attaches to the same bubble below the
                  English so the student stays anchored to exam vocab. */}
              {msg.role === 'ai' && msg.text && !msg.interactiveComponent && (
                msg.amharicTranslation ? (
                  <div
                    className="tg-msg-amharic"
                    style={{ marginTop: 10, paddingTop: 10, borderTop: '1px solid rgba(0,0,0,0.08)' }}
                  >
                    <MarkdownRenderer>{msg.amharicTranslation}</MarkdownRenderer>
                  </div>
                ) : translatingIndex === i ? (
                  <div
                    style={{ marginTop: 10, display: 'flex', alignItems: 'center', gap: 6, opacity: 0.7, fontSize: 13 }}
                    role="status"
                    aria-live="polite"
                  >
                    <EthiopianFlag size={14} /> Translating to Amharic…
                  </div>
                ) : (
                  <button
                    onClick={() => handleTranslateAmharic(msg.text, i)}
                    disabled={sending || translatingIndex !== null}
                    style={{
                      marginTop: 10,
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: 4,
                      background: '#f0f9e8',
                      color: '#3f6212',
                      border: '1px solid #d3e8b8',
                      borderRadius: 14,
                      padding: '4px 10px',
                      fontSize: 13,
                      cursor: 'pointer',
                    }}
                  >
                    <EthiopianFlag size={14} /> በአማርኛ
                  </button>
                )
              )}
            </div>
          </div>
        ))}

        {/* Typing indicator */}
        {sending && (
          <div className="tg-msg tg-msg-ai">
            <span className="tg-msg-avatar">🍋</span>
            <div className="tg-msg-bubble tg-typing">
              <span /><span /><span />
            </div>
          </div>
        )}

        <div ref={chatEndRef} />
      </div>

      {/* Input bar */}
      <div className="tg-lomi-input">
        {usage && !usage.can_send && (
          <p className="tg-usage-warn">Daily limit reached ({usage.messages_limit} messages)</p>
        )}
        <div className="tg-input-row">
          <textarea
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder={term ? `Ask about ${term}...` : topic ? `Ask about ${topic}...` : 'Ask Lomi anything...'}
            disabled={sending || !!(usage && !usage.can_send)}
            rows={2}
          />
          <button
            className="tg-send-btn"
            onClick={() => sendMessage(input)}
            disabled={!input.trim() || sending || !!(usage && !usage.can_send)}
          >
            ➤
          </button>
        </div>
      </div>
    </div>
  );
};

export default TelegramAskLomi;
